import { useState, useEffect, useRef, useCallback } from "react";
import { destinations } from "@/data/destinations";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";

// Search destinations by city, country, region, or tags
const searchDestinationsData = (query) => {
  if (!query || query.length < 2) return [];
  
  const searchTerm = query.toLowerCase().trim();
  
  return destinations
    .filter(dest => 
      dest.city.toLowerCase().includes(searchTerm) ||
      dest.country.toLowerCase().includes(searchTerm) ||
      dest.region.toLowerCase().includes(searchTerm) ||
      dest.tags.some(tag => tag.toLowerCase().includes(searchTerm))
    )
    .slice(0, 8);
};

export const DestinationAutocomplete = ({ 
  value, 
  onChange,
  onSelect,
  placeholder = "Where to?",
  id,
  "data-testid": testId,
  wrapperClassName = "",
  inputClassName = "",
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync with external value changes
  useEffect(() => {
    if (value !== inputValue && value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  const handleSearch = useCallback((query) => {
    const results = searchDestinationsData(query);
    setSuggestions(results);
    setIsOpen(results.length > 0);
    setHighlightedIndex(-1);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      handleSearch(newValue);
    }, 300);
  };

  const handleSelect = (destination) => {
    const displayValue = `${destination.city}, ${destination.country}`;
    setInputValue(displayValue);
    onChange?.(displayValue);
    onSelect?.(destination);
    setIsOpen(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelect(suggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
    }
  };

  const highlightMatch = (text, query) => {
    if (!query || query.length < 2) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, i) => 
      regex.test(part) ? (
        <span key={i} className="font-semibold text-accent">{part}</span>
      ) : (
        <span key={i}>{part}</span>
      )
    );
  };

  return (
    <div ref={wrapperRef} className={`relative ${wrapperClassName}`}>
      <input
        ref={inputRef}
        id={id}
        data-testid={testId}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
        placeholder={placeholder}
        autoComplete="off"
        className={inputClassName || "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-[#090e4c] transition-colors bg-white"}
      />
      
      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-[9999] w-full min-w-[280px] mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-72 overflow-y-auto">
          {suggestions.map((destination, index) => (
            <button
              key={`${destination.city}-${destination.country}-${index}`}
              type="button"
              className={`w-full px-4 py-3 text-left flex items-start gap-3 transition-colors ${index === highlightedIndex ? "bg-blue-50" : "hover:bg-gray-50"}`}
              onClick={() => handleSelect(destination)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <MapPin className="h-4 w-4 text-[#21b3ed] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">
                  {highlightMatch(destination.city, inputValue)}, {destination.country}
                </div>
                <div className="text-xs text-gray-400 flex items-center gap-2">
                  <span>{destination.region}</span>
                  <span className="text-[#21b3ed]">•</span>
                  <span className="truncate">{destination.tags.slice(0, 3).join(", ")}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
